import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * Amavra Night Flow offer integration for Horizon.
 *
 * Offer: #amv-bundle contains .amv-kb-bar cards with data-variant-id,
 * data-selling-plan, data-price and data-sub-price (integer cents),
 * data-bottles and data-months. Optional root data-currency overrides Shopify.
 * Subscription: #amv-kb-subrow.amv-on; button: #amv-bd-atc;
 * product error: #amv-cart-error. Optional interval target: [data-amv-interval].
 *
 * Drawer buttons: data-amv-cart-change, data-key (real Shopify line key),
 * plus data-delta="1|-1" OR data-quantity="0" for removal.
 * Quantity always means whole bundles. Paid/free rows are presentation only.
 * Both ADD and CHANGE share one queue. Deltas use the fresh cart at execution,
 * never the stale DOM quantity. Checkout waits for that queue and checks the
 * fresh form after hydration before navigation. ADD is never retried here.
 */
const BOTTLES_BY_VARIANT = new Map([
  [46156770934863, 1],
  [46156770967631, 3],
  [46156771000399, 5],
]);
const DRAWER_SECTION = 'cart-drawer-section';
const NIGHT_FLOW_PRODUCT_ID = '8946998870095';
let cartQueue = Promise.resolve();
const addingRoots = new WeakSet();
let cleanupStickyVisibility;
let cartFailureSerial = 0;
let lastCartMutationError = '';
let checkoutRequested = false;

function enqueue(task) {
  const result = cartQueue.then(task);
  cartQueue = result.catch(() => {});
  return result;
}

function recordCartFailure(error) {
  cartFailureSerial += 1;
  lastCartMutationError = error?.message || 'Your cart could not be updated. Please review it before checking out.';
}

function route(path) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.replace(/\/?$/, '/')}${path}`;
}

function apiMessage(data, response) {
  const value = data?.description || data?.message || data?.errors;
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return Object.values(value).flat().join(' ');
  }
  return `Cart request failed (${response.status}). Please try again.`;
}

async function request(path, body) {
  const response = await fetch(route(path), {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(`Cart request returned an invalid response (${response.status}).`);
  }
  if (!response.ok || data?.errors || Number(data?.status) >= 400) {
    throw new Error(apiMessage(data, response));
  }
  return data;
}

function sectionRequest() {
  const sections = new Set([DRAWER_SECTION]);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component.dataset.sectionId) sections.add(component.dataset.sectionId);
  });
  return {
    sections: [...sections].slice(0, 5).join(','),
    sections_url: window.location.pathname + window.location.search,
  };
}

async function reconcileDrawer(cart) {
  // A zero-quantity or already-removed line can still exist in old section HTML.
  // Repair that view with a fresh read, without issuing a redundant cart write.
  const html = await sectionRenderer.getSectionHTML(DRAWER_SECTION, false);
  await morphSection(DRAWER_SECTION, html, { mode: 'hydration', injectStylesheet: true });
  updateCartBubbles(cart.items);
}

function clearErrors() {
  document.querySelectorAll('#amv-cart-error, #amv-drawer-error, [data-amv-cart-error]').forEach((element) => {
    element.textContent = '';
    element.hidden = true;
  });
}

function showError(message, inDrawer = false) {
  let target = inDrawer
    ? document.querySelector('#cart-drawer #amv-drawer-error, #cart-drawer [data-amv-cart-error]')
    : document.getElementById('amv-cart-error');
  if (!target && inDrawer) {
    const parent = document.querySelector('#cart-drawer .cart-drawer__summary')
      || document.querySelector('#cart-drawer .cart-drawer__content');
    if (parent) {
      target = document.createElement('p');
      target.dataset.amvCartError = '';
      target.className = 'amv-cart-error';
      target.style.cssText = 'color:#a32323;font-size:13px;line-height:1.4;margin:8px 0;width:100%';
      parent.prepend(target);
    }
  }
  if (!target) target = document.getElementById('amv-cart-error');
  if (target) {
    target.setAttribute('role', 'alert');
    target.textContent = message;
    target.hidden = false;
  }
}

function startEvent(action, lines, context) {
  const deferred = CartLinesUpdateEvent.createPromise();
  // Keep a handled rejection even on pages where no native cart has upgraded yet.
  deferred.promise.catch(() => {});
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));
  return deferred;
}

async function readyNativeCart() {
  if (document.querySelector('cart-items-component')) {
    await customElements.whenDefined('cart-items-component');
  }
}

function finishEvent(deferred, cart, sections) {
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      itemCount: cart.item_count,
      sections,
      source: 'amavra-offer',
      didError: false,
    },
  });
}

async function openDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) throw new Error('Your items are in the cart, but the cart drawer is unavailable.');
  await customElements.whenDefined('theme-drawer');
  // Native cart-items-component consumes the event promise and hydrates in place.
  await new Promise((resolve) => requestAnimationFrame(resolve));
  if (typeof drawer.open !== 'function') throw new Error('Your items are in the cart, but the cart drawer could not open.');
  drawer.open();
}

function physicalCount(items) {
  return items.reduce((count, item) => count + item.quantity * (BOTTLES_BY_VARIANT.get(Number(item.variant_id || item.id)) || 1), 0);
}

function updateCartBubbles(items) {
  const count = physicalCount(items);
  // Keep StandardEvents' actual bundle quantity intact for analytics/integrations.
  // Run after native promise callbacks, which otherwise show Shopify's bundle count.
  customElements.whenDefined('cart-icon').then(() => {
    queueMicrotask(() => {
      document.querySelectorAll('cart-icon').forEach((icon) => {
        if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false);
      });
    });
  });
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(({ detail }) => {
    if (Array.isArray(detail?.items)) updateCartBubbles(detail.items);
    else request('cart.js').then((cart) => updateCartBubbles(cart.items)).catch(() => {});
  }).catch(() => {});
});

function cents(value) {
  if (value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? Math.round(amount) : null;
}

function money(amount, root) {
  const currency = root.dataset.currency || window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount / 100);
}

function refreshStickyOffer(root, selected, subscribed) {
  if (!selected || !BOTTLES_BY_VARIANT.has(Number(selected.dataset.variantId))) return;
  const price = (subscribed && selected.dataset.sellingPlan ? cents(selected.dataset.subPrice) : null)
    ?? cents(selected.dataset.price);
  if (price === null) return;
  const compare = cents(selected.dataset.comparePrice);
  const compareLabel = compare !== null ? money(compare, root) : selected.querySelector('.amv-kb-fullprice')?.textContent;
  const title = selected.querySelector('.amv-kb-title')?.textContent?.trim() || `${selected.dataset.bottles} Bottles`;
  document.querySelectorAll(`sticky-add-to-cart[data-product-id="${NIGHT_FLOW_PRODUCT_ID}"]`).forEach((sticky) => {
    sticky.dataset.currentVariantId = selected.dataset.variantId;
    sticky.querySelector('[ref="addToCartButton"]')?.setAttribute('aria-label', `Add to cart: ${title}${subscribed ? ', automatic refills' : ''}`);
    const variantTitle = sticky.querySelector('.sticky-add-to-cart__variant');
    if (variantTitle) variantTitle.textContent = `${title}${subscribed ? ' · Automatic refills' : ''}`;
    sticky.querySelectorAll('.sticky-add-to-cart__price .price__regular .price, .sticky-add-to-cart__price .price-item--sale').forEach((element) => {
      element.textContent = money(price, root);
    });
    if (compareLabel) {
      sticky.querySelectorAll('.sticky-add-to-cart__price .compare-at-price').forEach((element) => {
        element.textContent = compareLabel;
      });
    }
  });
}

function setupStickyVisibility(root) {
  cleanupStickyVisibility?.();
  cleanupStickyVisibility = undefined;
  const button = root?.querySelector('#amv-bd-atc');
  if (!button) return;
  const bars = [...document.querySelectorAll(`sticky-add-to-cart[data-product-id="${NIGHT_FLOW_PRODUCT_ID}"]`)]
    .filter((sticky) => {
      const nativeForm = sticky.closest('.shopify-section')?.querySelector(`product-form-component[data-product-id="${NIGHT_FLOW_PRODUCT_ID}"]`);
      // Preserve the native observer wherever a real buy-buttons block exists.
      return !nativeForm?.closest('.buy-buttons-block');
    })
    .map((sticky) => sticky.querySelector('[ref="stickyBar"]'))
    .filter(Boolean);
  if (!bars.length) return;
  let frame;
  const update = () => {
    frame = undefined;
    if (!button.isConnected) return;
    const pastOffer = button.getBoundingClientRect().bottom < 0;
    bars.forEach((bar) => { bar.dataset.stuck = String(pastOffer); });
  };
  const schedule = () => {
    if (frame === undefined) frame = requestAnimationFrame(update);
  };
  const observer = new IntersectionObserver(schedule);
  observer.observe(button);
  // Also handle anchor jumps that skip the button's intersection entirely.
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  update();
  cleanupStickyVisibility = () => {
    observer.disconnect();
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    if (frame !== undefined) cancelAnimationFrame(frame);
  };
}

function refreshOffer(root) {
  if (!root) return;
  const subscription = root.querySelector('#amv-kb-subrow');
  const subscribed = subscription?.classList.contains('amv-on') || false;
  subscription?.setAttribute('aria-checked', String(subscribed));
  const cards = [...root.querySelectorAll('.amv-kb-bar')];
  const selected = cards.find((card) => card.classList.contains('amv-sel')) || cards[0];
  for (const card of cards) {
    const active = card === selected;
    card.classList.toggle('amv-sel', active);
    card.setAttribute('aria-checked', String(active));
    if (card.getAttribute('role') === 'radio') card.tabIndex = active ? 0 : -1;
    const price = (subscribed && card.dataset.sellingPlan ? cents(card.dataset.subPrice) : null)
      ?? cents(card.dataset.price);
    if (price === null) continue;
    const priceElement = card.querySelector('.amv-kb-price');
    if (priceElement) priceElement.textContent = money(price, root);
    const perBottle = card.querySelector('.amv-kb-sub');
    const bottles = Number(card.dataset.bottles) || BOTTLES_BY_VARIANT.get(Number(card.dataset.variantId)) || 1;
    if (perBottle) perBottle.textContent = `${money(Math.round(price / bottles), root)} per bottle`;
  }
  const months = Number(selected?.dataset.months) || 1;
  const interval = subscription?.querySelector('[data-amv-interval], .amv-kb-subsub');
  if (interval) interval.textContent = `Shipped every ${months === 1 ? 'month' : `${months} months`}. No commitment. Cancel anytime`;
  refreshStickyOffer(root, selected, subscribed);
}

function selectCard(root, card, focus = false) {
  root.querySelectorAll('.amv-kb-bar').forEach((element) => element.classList.toggle('amv-sel', element === card));
  refreshOffer(root);
  clearErrors();
  if (focus) card.focus();
}

async function addOffer(root) {
  if (addingRoots.has(root)) return;
  const selected = root.querySelector('.amv-kb-bar.amv-sel');
  const variantId = Number(selected?.dataset.variantId);
  const subscribed = root.querySelector('#amv-kb-subrow')?.classList.contains('amv-on');
  const sellingPlan = Number(selected?.dataset.sellingPlan);
  if (!Number.isSafeInteger(variantId) || variantId <= 0) {
    showError('Please select an offer before adding it to your cart.');
    return;
  }
  if (subscribed && (!Number.isSafeInteger(sellingPlan) || sellingPlan <= 0)) {
    showError('Automatic refills are unavailable for this offer. Please choose a one-time purchase or another offer.');
    return;
  }
  const button = root.querySelector('#amv-bd-atc');
  const originalLabel = button?.innerHTML;
  const originallyDisabled = button?.disabled || false;
  addingRoots.add(root);
  clearErrors();
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const label = button.querySelector('.t');
    if (label) label.textContent = 'Adding…';
  }
  // Capture the selected offer now: later card toggles cannot change a queued ADD.
  const item = { id: variantId, quantity: 1, ...(subscribed ? { selling_plan: sellingPlan } : {}) };
  try {
    await enqueue(async () => {
      await readyNativeCart();
      const deferred = startEvent('add', [{ merchandiseId: String(variantId), quantity: 1 }], 'product');
      let added = false;
      let sections;
      try {
        const response = await request('cart/add.js', { items: [item], ...sectionRequest() });
        added = true;
        sections = response.sections;
        const cart = await request('cart.js');
        finishEvent(deferred, cart, sections);
        await openDrawer();
      } catch (error) {
        deferred.reject(error);
        recordCartFailure(error);
        if (added) {
          // The ADD succeeded. Only recover its returned markup; never repeat ADD.
          if (sections?.[DRAWER_SECTION]) {
            try {
              await morphSection(DRAWER_SECTION, sections[DRAWER_SECTION], { mode: 'hydration', injectStylesheet: true });
              await openDrawer();
            } catch { /* Keep the explicit post-ADD error below. */ }
          }
          showError(`Your offer was added to the cart. ${error.message || 'The cart display could not refresh.'} Please review your cart before adding again.`, true);
        } else {
          showError(error.message || 'Could not add this offer. Please try again.');
        }
      }
    });
  } finally {
    addingRoots.delete(root);
    if (button) {
      button.disabled = originallyDisabled;
      button.removeAttribute('aria-busy');
      if (originalLabel !== undefined) button.innerHTML = originalLabel;
    }
  }
}

function changeQuantity(button) {
  const key = button.dataset.key;
  const delta = button.hasAttribute('data-delta') ? Number(button.dataset.delta) : null;
  const absolute = button.hasAttribute('data-quantity') ? Number(button.dataset.quantity) : null;
  if (!key || (delta === null && absolute === null)
    || (delta !== null && !Number.isSafeInteger(delta))
    || (absolute !== null && (!Number.isSafeInteger(absolute) || absolute < 0))) return;
  // Capture intent before native hydration replaces this button.
  enqueue(async () => {
    clearErrors();
    let deferred;
    try {
      const current = await request('cart.js');
      const line = current.items.find((item) => item.key === key);
      // The mutation can already be fulfilled while the displayed row is stale.
      if (!line) {
        await reconcileDrawer(current);
        return;
      }
      const quantity = Math.max(0, delta !== null ? line.quantity + delta : absolute);
      if (quantity === line.quantity) {
        await reconcileDrawer(current);
        return;
      }
      await readyNativeCart();
      deferred = startEvent(quantity === 0 ? 'remove' : 'update', [{ id: key, quantity }], 'cart');
      const cart = await request('cart/change.js', { id: key, quantity, ...sectionRequest() });
      finishEvent(deferred, cart, cart.sections);
    } catch (error) {
      deferred?.reject(error);
      recordCartFailure(error);
      showError(error.message || 'Could not update your cart. Please try again.', true);
    }
  });
}

async function handleCheckout(event) {
  const originalForm = event.target;
  if (!(originalForm instanceof HTMLFormElement)
    || !originalForm.matches('.amv-cart-checkout-form')
    || !originalForm.closest('#cart-drawer')) return;
  event.preventDefault();
  if (checkoutRequested) return;
  checkoutRequested = true;
  const failureAtClick = cartFailureSerial;
  try {
    // Keep waiting if another quantity intent joined the queue meanwhile.
    let pending;
    do {
      pending = cartQueue;
      await pending;
    } while (pending !== cartQueue);
    if (cartFailureSerial !== failureAtClick) {
      // Surface the failed intent first. A later deliberate checkout click can
      // still purchase the cart as it currently stands; no button is disabled.
      showError(lastCartMutationError, true);
      return;
    }
    // A successful quantity response may have replaced the submitted form.
    const form = document.querySelector('#cart-drawer .amv-cart-checkout-form');
    const submitter = form?.querySelector('[type="submit"][name="checkout"]');
    if (!form || !submitter) {
      showError('Your cart is empty. Add an offer before checking out.', true);
      return;
    }
    // Start Shopify checkout only after all cart writes and fresh-form checks.
    // Re-dispatching submit here can be intercepted again by storefront scripts.
    window.location.assign(route('checkout'));
  } catch (error) {
    showError(error.message || 'Checkout could not start. Please try again.', true);
  } finally {
    checkoutRequested = false;
  }
}

document.addEventListener('submit', handleCheckout);

// The native sticky handler puppets a separate product-form with its original
// variant/plan. Capture this specific Night Flow button before that handler runs,
// and use the same selected-offer path as the main custom button.
window.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const stickyButton = event.target.closest(`sticky-add-to-cart[data-product-id="${NIGHT_FLOW_PRODUCT_ID}"] [ref="addToCartButton"]`);
  const root = document.getElementById('amv-bundle');
  if (!stickyButton || !root) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  addOffer(root);
}, { capture: true });

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const changeButton = event.target.closest('[data-amv-cart-change]');
  if (changeButton?.closest('#cart-drawer')) {
    event.preventDefault();
    changeQuantity(changeButton);
    return;
  }
  const root = event.target.closest('#amv-bundle');
  if (!root) return;
  if (event.target.closest('#amv-bd-atc')) {
    event.preventDefault();
    addOffer(root);
    return;
  }
  const card = event.target.closest('.amv-kb-bar');
  if (card) selectCard(root, card);
  else if (event.target.closest('#amv-kb-subrow')) {
    root.querySelector('#amv-kb-subrow').classList.toggle('amv-on');
    refreshOffer(root);
    clearErrors();
  }
});

document.addEventListener('keydown', (event) => {
  if (!(event.target instanceof Element)) return;
  const root = event.target.closest('#amv-bundle');
  if (!root) return;
  const checkbox = event.target.closest('#amv-kb-subrow[role="checkbox"]');
  if (checkbox && (event.key === ' ' || event.key === 'Enter')) {
    event.preventDefault();
    checkbox.click();
    return;
  }
  const card = event.target.closest('.amv-kb-bar[role="radio"]');
  if (!card) return;
  const cards = [...root.querySelectorAll('.amv-kb-bar')];
  const index = cards.indexOf(card);
  let next;
  if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = cards[(index + 1) % cards.length];
  if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = cards[(index - 1 + cards.length) % cards.length];
  if (event.key === 'Home') next = cards[0];
  if (event.key === 'End') next = cards[cards.length - 1];
  if (event.key === ' ' || event.key === 'Enter') next = card;
  if (next) {
    event.preventDefault();
    selectCard(root, next, true);
  }
});

refreshOffer(document.getElementById('amv-bundle'));
setupStickyVisibility(document.getElementById('amv-bundle'));
document.addEventListener('shopify:section:load', () => {
  const root = document.getElementById('amv-bundle');
  refreshOffer(root);
  setupStickyVisibility(root);
});
document.addEventListener(StandardEvents.productSelect, (event) => {
  // Native product selection can morph sticky contents after the initial render.
  event.promise?.then(() => {
    queueMicrotask(() => refreshOffer(document.getElementById('amv-bundle')));
  }).catch(() => {});
});

function refreshTimer() {
  const timer = document.getElementById('amv-bd-timer');
  if (!timer) return;
  const now = new Date();
  const midnight = new Date();
  midnight.setHours(23, 59, 59, 999);
  const remaining = Math.max(0, midnight - now);
  const hours = Math.floor(remaining / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  timer.textContent = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
}
refreshTimer();
setInterval(refreshTimer, 1000);

// Initial native header markup counts bundles; render the matching bottle count.
enqueue(() => request('cart.js').then((cart) => updateCartBubbles(cart.items))).catch(() => {});
